<template>
  <div :class="rootClasses">
    <label v-if="$slots.label" :id="`label-${id}`" :for="$attrs?.inputId as string">
      <slot name="label" />
    </label>

    <!-- PrimeVue InputNumber has an weird intended behaviour by them which only validates and update the modelValue by:
      1 - pressing enter
      2 - on blur
      3 - using arrows or the increase/decrease buttons

      Due that community opened several issues related to that, but they intend to keep this behaviour,
      but they added the @input callback to do what the dev needs.
    -->
    <PVInputNumber
      :id="id"
      v-model="value"
      :placeholder="placeholder"
      :data-unit="unit || undefined"
      v-bind="$attrs"
      @focus="focused = true"
      @blur="focused = false"
      @input="checkInput"
      ref="pvInputNumberRef"
      :pt="mergedPt"
    >
      <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
        <slot v-if="slot" :name="slot" v-bind="scope" />
      </template>
    </PVInputNumber>

    <slot v-if="$slots.endAdornment" name="endAdornment" />

    <span v-if="$slots.rightSideLabel" class="ml-2">
      <slot name="rightSideLabel" />
    </span>

    <span v-if="$slots.helperText" class="caption" :id="`helper-text-${id}`">
      <slot name="helperText" />
    </span>
  </div>
</template>

<script lang="ts">
export default { name: 'InputNumber', inheritAttrs: false };
</script>

<script setup lang="ts">
import { computed, useAttrs, ref, useSlots } from 'vue';
import PVInputNumber, { type InputNumberInputEvent } from 'primevue/inputnumber';
import { useInputState } from '../composables';
import { useElementSize } from '@vueuse/core';
import type { ClassType } from '../composables/input/use-input-state';

defineProps({
  id: {
    type: String,
    default: 'number-input',
  },
  placeholder: String,
  /**
   * Unit text pinned inside the input's right edge, e.g. "min"
   */
  unit: String,
});

const attrs = useAttrs();
const slots = useSlots();

const pvInputNumberRef = ref();
const { height, width } = useElementSize(pvInputNumberRef);

const value = defineModel({ type: Number });

const { focused, classes } = useInputState(attrs);

const mergedPt = computed(() => {
  const attrPt = (attrs.pt ?? {}) as Record<string, unknown>;
  return {
    incrementButton: {
      'aria-label': 'Increment input number', // i18n to be implemented in CPLAT-643
      'data-testid': 'increment-input-number-button',
    },
    decrementButton: {
      'aria-label': 'Decrement input number', // i18n to be implemented in CPLAT-643
      'data-testid': 'decrement-input-number-button',
    },
    ...attrPt,
  };
});

// @ts-expect-error FIXME: No overload matches this call
const rootClasses = computed<ClassType[]>(() => [
  ...classes.value,
  { 'p-input-icon-right': slots.endAdornment },
]);

const isDirty = ref<boolean>(false);
const checkInput = (event: InputNumberInputEvent) => {
  isDirty.value = true;
  if (event.value != null) value.value = Number(event.value);
};

defineExpose({ value, isDirty, height, width });
</script>

<style scoped lang="scss">
.p-inputnumber[data-unit] {
  position: relative;

  &::after {
    content: attr(data-unit);
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-color-secondary);
    pointer-events: none;
  }

  // Must clear the widest unit text (fr "commandes") so typed values scroll instead of
  // rendering underneath it
  :deep(.p-inputtext) {
    padding-right: 6.5rem;
  }
}
</style>
